import { useState, useEffect, useMemo } from "react";
import { BsStarFill } from "react-icons/bs";
import { RiVerifiedBadgeFill } from "react-icons/ri";
import { AiOutlineLeft, AiOutlineRight, AiOutlineClose } from "react-icons/ai";

const LEVELS = [
  { stars: 5, label: "Excellent", bar: "bg-green-600" },
  { stars: 4, label: "Very Good", bar: "bg-green-500" },
  { stars: 3, label: "Good", bar: "bg-yellow-400" },
  { stars: 2, label: "Average", bar: "bg-orange-400" },
  { stars: 1, label: "Poor", bar: "bg-red-500" },
];

const badgeColor = (r) =>
  r >= 4 ? "bg-green-600" : r >= 3 ? "bg-yellow-500" : r >= 2 ? "bg-orange-500" : "bg-red-500";

const RatingBadge = ({ value }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold text-white ${badgeColor(value)}`}
  >
    {Number(value).toFixed(1)} <BsStarFill className="text-[10px]" />
  </span>
);

const formatDate = (d) =>
  new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const MAX_THUMBS = 5;

const ReviewItem = ({ review, onImageClick }) => {
  const images = review.productImage || [];
  const shown = images.slice(0, MAX_THUMBS);
  const extra = images.length - MAX_THUMBS;

  return (
    <div className="py-4 border-b border-gray-200 last:border-b-0">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-full bg-yellow-100 text-yellow-800 flex items-center justify-center text-xs font-semibold">
          {review.userId?.name?.charAt(0)?.toUpperCase() || "U"}
        </div>
        <span className="text-sm font-medium text-gray-800">{review.userId?.name || "User"}</span>
        {review.verified && (
          <span className="flex items-center text-xs text-green-600">
            <RiVerifiedBadgeFill className="mr-0.5" />
            Verified Purchase
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-2">
        <RatingBadge value={review.ratings} />
        <span className="text-xs text-gray-500">Posted on {formatDate(review.createdAt)}</span>
      </div>

      {review.reviews && <p className="text-sm text-gray-700 leading-6">{review.reviews}</p>}

      {shown.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {shown.map((image, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => onImageClick(images, idx)}
              className="relative w-16 h-16 rounded-md overflow-hidden bg-gray-100 border border-gray-200 hover:opacity-90"
            >
              <img src={image} alt={`Review ${idx + 1}`} className="w-full h-full object-cover" />
              {idx === MAX_THUMBS - 1 && extra > 0 && (
                <span className="absolute inset-0 bg-black/60 text-white text-sm font-semibold flex items-center justify-center">
                  +{extra}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ReviewList = ({ reviews = [], onOpenReviewForm, previewCount = 3 }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, images: [], index: 0 });

  const { average, counts, total } = useMemo(() => {
    const counts = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    let sum = 0;
    reviews.forEach((r) => {
      const v = Math.min(5, Math.max(1, Math.round(r.ratings || 0)));
      counts[v] += 1;
      sum += r.ratings || 0;
    });
    return {
      average: reviews.length ? sum / reviews.length : 0,
      counts,
      total: reviews.length,
    };
  }, [reviews]);

  // Lock page scroll while drawer / lightbox is open
  useEffect(() => {
    if (!drawerOpen && !lightbox.open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [drawerOpen, lightbox.open]);

  const openLightbox = (images, index) => setLightbox({ open: true, images, index });
  const closeLightbox = () => setLightbox({ open: false, images: [], index: 0 });
  const stepLightbox = (dir) =>
    setLightbox((p) => ({
      ...p,
      index: dir === "prev" ? (p.index === 0 ? p.images.length - 1 : p.index - 1) : p.index === p.images.length - 1 ? 0 : p.index + 1,
    }));

  return (
    <div id="reviews" className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">Product Ratings &amp; Reviews</h2>
        <button
          type="button"
          onClick={onOpenReviewForm}
          className="text-sm font-semibold text-yellow-800 border border-yellow-800 rounded-md px-3 py-1.5 hover:bg-yellow-50"
        >
          Write a Review
        </button>
      </div>

      {total === 0 ? (
        <p className="text-sm text-gray-500 py-6 text-center">
          No reviews yet. Be the first to share your experience.
        </p>
      ) : (
        <>
          {/* Summary */}
          <div className="flex items-center gap-6 sm:gap-10 pb-4 border-b border-gray-200">
            <div className="shrink-0">
              <div className="flex items-center gap-1 text-green-600">
                <span className="text-4xl font-bold leading-none">{average.toFixed(1)}</span>
                <BsStarFill className="text-xl" />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {total} {total === 1 ? "Review" : "Reviews"}
              </p>
            </div>

            <div className="flex-1 space-y-1.5">
              {LEVELS.map(({ stars, label, bar }) => (
                <div key={stars} className="flex items-center gap-3 text-xs">
                  <span className="w-16 text-right text-gray-700">{label}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className={`h-full rounded-full ${bar}`}
                      style={{ width: `${(counts[stars] / total) * 100}%` }}
                    />
                  </div>
                  <span className="w-8 text-gray-500">{counts[stars]}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Preview reviews */}
          <div>
            {reviews.slice(0, previewCount).map((review) => (
              <ReviewItem key={review._id} review={review} onImageClick={openLightbox} />
            ))}
          </div>

          {total > previewCount && (
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="mt-1 text-sm font-semibold text-yellow-800 hover:underline"
            >
              VIEW ALL REVIEWS &rsaquo;
            </button>
          )}
        </>
      )}

      {/* All reviews drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <aside className="relative w-full sm:max-w-xl h-full bg-white overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-5 py-4 flex items-start justify-between gap-4">
              <h3 className="text-sm font-semibold text-gray-900">
                All reviews ({total})
              </h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setDrawerOpen(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                <AiOutlineClose size={20} />
              </button>
            </div>
            <div className="px-5">
              {reviews.map((review) => (
                <ReviewItem key={review._id} review={review} onImageClick={openLightbox} />
              ))}
            </div>
          </aside>
        </div>
      )}

      {/* Image lightbox */}
      {lightbox.open && (
        <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col items-center justify-center p-4">
          <button
            type="button"
            aria-label="Close"
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full"
          >
            <AiOutlineClose size={24} />
          </button>

          <div className="relative w-full max-w-3xl h-[70vh] flex items-center justify-center">
            {lightbox.images.length > 1 && (
              <button
                type="button"
                aria-label="Previous"
                onClick={() => stepLightbox("prev")}
                className="absolute left-0 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 z-10"
              >
                <AiOutlineLeft size={22} />
              </button>
            )}
            <img
              src={lightbox.images[lightbox.index]}
              alt={`Review ${lightbox.index + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            {lightbox.images.length > 1 && (
              <button
                type="button"
                aria-label="Next"
                onClick={() => stepLightbox("next")}
                className="absolute right-0 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 z-10"
              >
                <AiOutlineRight size={22} />
              </button>
            )}
          </div>
          <p className="mt-3 text-sm text-white">
            {lightbox.index + 1} of {lightbox.images.length}
          </p>
        </div>
      )}
    </div>
  );
};

export default ReviewList;